import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  type Firestore,
} from 'firebase/firestore';
import type { Tenant } from '@kawai/shared';
import { db as defaultDb } from '../firebase.js';

/**
 * S-05: tenants collection 全件取得（read 専用 / saas-admin 用）。
 *
 * - Firestore Rules (T-04) で `tenants` の read は saas-admin のみ許可。
 *   tenant-admin / tenant-staff が呼ぶと permission-denied になる
 * - 引数 `db` はテスト時に Firestore モックを注入するため optional
 * - 並びは UI 側でソート（ここでは id asc で安定化のみ）
 */
export async function fetchAllTenants(db: Firestore = defaultDb): Promise<Tenant[]> {
  const snap = await getDocs(query(collection(db, 'tenants')));
  const items: Tenant[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Tenant, 'id'>),
  }));
  return items.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * 単一テナント doc 取得（read 専用 / saas-admin 用）。
 *
 * - 明細ページ（`/billing/:tenantId`）のように 1 テナントだけ要るときに、全件取得を避けて O(1) read にする
 * - Rules (T-04) で `tenants` の read は saas-admin のみ許可
 * - 存在しなければ null（呼び出し側で「見つかりません」を表示）
 * - 引数 `db` はテスト時に Firestore モックを注入するため optional
 */
export async function fetchTenantById(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<Tenant | null> {
  const snap = await getDoc(doc(db, 'tenants', tenantId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Tenant, 'id'>) };
}
